import { useSyncExternalStore } from "react";

/**
 * Add to Home Screen (PWA phase 1). Chrome on Android offers its own install prompt
 * (`beforeinstallprompt`), kept here from the page's first load; iOS has none, so its
 * card says the Share sheet's steps instead. Installed, or dismissed once, it never shows.
 */
export type InstallWay = "prompt" | "ios" | null;

/** How this browser can install Crew, if it should be offered at all. */
export function installWay(b: {
  standalone: boolean;
  dismissed: boolean;
  canPrompt: boolean;
  userAgent: string;
  touchPoints: number;
}): InstallWay {
  if (b.standalone || b.dismissed) return null;
  if (b.canPrompt) return "prompt";
  return isIos(b.userAgent, b.touchPoints) ? "ios" : null;
}

/** An iPhone, or an iPad (which says it's a Mac, with a touch screen). */
export function isIos(userAgent: string, touchPoints: number): boolean {
  return /iPhone|iPad|iPod/.test(userAgent) || (/Macintosh/.test(userAgent) && touchPoints > 1);
}

/** Running from the home screen, not in a browser tab. */
export function isStandalone(): boolean {
  if (typeof window === "undefined") return false;
  const nav = navigator as Navigator & { standalone?: boolean };
  return nav.standalone === true || window.matchMedia("(display-mode: standalone)").matches;
}

type PromptEvent = Event & { prompt: () => Promise<void>; userChoice: Promise<unknown> };

const DISMISSED = "crew:install:dismissed";
let deferred: PromptEvent | null = null;
let dismissed = readDismissed();
const listeners = new Set<() => void>();
const changed = () => listeners.forEach((l) => l());

function readDismissed(): boolean {
  try {
    return globalThis.localStorage?.getItem(DISMISSED) === "1";
  } catch {
    return false;
  }
}

/** From main.tsx, before React: Chrome fires its prompt once, often before Chats mounts. */
export function listenForInstall() {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    deferred = e as PromptEvent;
    changed();
  });
  window.addEventListener("appinstalled", () => {
    deferred = null;
    changed();
  });
}

/** The service worker that makes Crew installable; only in a built app, never under Vite. */
export function registerServiceWorker() {
  if (!import.meta.env.PROD || !("serviceWorker" in navigator)) return;
  void navigator.serviceWorker.register("/hq/sw.js", { scope: "/hq/" }).catch(() => {});
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

const snapshot = (): InstallWay =>
  installWay({
    standalone: isStandalone(),
    dismissed,
    canPrompt: deferred !== null,
    userAgent: navigator.userAgent,
    touchPoints: navigator.maxTouchPoints,
  });

export function useInstall() {
  const way = useSyncExternalStore(subscribe, snapshot, () => null);
  return {
    way,
    /** Chrome's own install sheet; whatever the answer, the card goes for this visit. */
    install: async () => {
      const e = deferred;
      if (!e) return;
      deferred = null;
      dismissed = true;
      changed();
      await e.prompt().catch(() => {});
    },
    dismiss: () => {
      dismissed = true;
      try {
        globalThis.localStorage?.setItem(DISMISSED, "1");
      } catch {
        // A private window: dismissed for this visit only.
      }
      changed();
    },
  };
}
